import { t } from '../../utils/i18n';
import {
    appointmentDateRange,
    primaryColor,
    procedureIdSelected,
    proceduresOfAppointmentChannel,
    providerId,
    setAppointmentDateRange,
    setLimitRightCarousel,
    selectedProcedures,
    selectedAddons,
    requestsGetSlotsTime,
    setRequestsGetSlotsTime,
    nextRequestsGetSlotsTime,
    setNextRequestsGetSlotsTime,
    setDataSlot,
    setSelectedAppointmentUnixTime,
    isBookAppointment,
    locationId,
    setLocationId,
    selectedDeliveryMode,
    form,
    appointmentChannelId,
    providers,
    selectedLocation,
    visitorId,
    shouldCollectPatientInfoFirstForWidget,
    leadInfo,
    setPhone,
    setSelectedCountry,
    isOTPTimeOut,
    isFlowForCalendar,
    staffId,
    setAssignedStaffId,
    isPhoneOtpEnabled,
    otpAccessToken,
    setAccessToken,
    setLeadInfo,
    setLeadTextId,
} from '../../utils/variables';
import {
    appointmentItemsClickEventHandler,
    clickDateCarouselItem,
    clickOnlyDateCarouselItem,
    getAppoitmentsDatetime,
    loadSpinnerIcon,
} from '../../modules/actions/selectTimeActions';
import { convertMonthFormat } from '../../utils/dateTimeHelpers';
import { getProviderFullName } from '../../modules/actions/providerActions';
import {
    getFullyBookedDays,
    getNextAvailableSlot,
    getTimeSlots,
} from '../../services/slotsServices';
import { newPhoneUI } from '../../modules/actions/phoneActions';
import { doFoCus, navigate, params } from '../../modules/events/movePages';
import { SCREEN } from '../../utils/constants';
import { phoneListeners } from '../../modules/events/phoneListeners';
import { updateVisitorAction } from '../../services/visitorServices';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { sendOTPCodeWithTimer, clearOTPTimer } from '../../modules/actions/validateDigitsActions';
import { createBookingToken } from '../../services/digitsCodeService';

/** The clinic's booking timezone (per-location, then channel-level fallback). */
function clinicTimezone() {
    return form?.appointment_channel?.location_availabilities
            ?.find((a) => a.location_id === selectedLocation)?.availability?.timezone
        || form?.appointment_channel?.availability_timezone
        || null;
}

/**
 * Whether a day cell is bookable, honoring the per-location `in_person_dates`
 * allow-list. Mirrors NativeScheduler::locationHoursForDay(): an in-person
 * request is bookable ONLY on allow-listed dates (which may fall outside the
 * weekly template, e.g. a visiting-provider Thursday); remote requests
 * (virtual/phone) keep the weekly template; without mode context, accept the
 * union of both — the server still validates the actual slots.
 * @param {number} unixTime - the cell's timestamp (viewer-local midnight)
 * @param {object|null} locationAvailability - the location_availabilities entry's `availability` JSON
 * @param {object|null} fallbackWeeklyHours - channel-level `availability_hours` fallback
 */
function dayCellIsAvailable(unixTime, locationAvailability, fallbackWeeklyHours) {
    const daysOfWeekNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const weekly = locationAvailability?.hours ?? fallbackWeeklyHours;
    const dayOfWeek = daysOfWeekNames[new Date(unixTime * 1000).getDay()];
    const weeklyOpen = !weekly || !!(weekly[dayOfWeek] && weekly[dayOfWeek].length > 0);

    const allowlist = locationAvailability?.in_person_dates;
    if (!allowlist || typeof allowlist !== 'object' || Array.isArray(allowlist) || Object.keys(allowlist).length === 0) {
        return weeklyOpen;
    }

    // Match the cell to the allow-list's clinic-local date the same way
    // markDaysWithNoSlotsDisabled does: anchor at midday so viewers far from
    // the clinic don't land on the previous clinic day.
    const tz = locationAvailability?.timezone || form?.appointment_channel?.availability_timezone || null;
    const middayMs = (unixTime + 43200) * 1000;
    let dateStr;
    try {
        dateStr = tz
            ? new Date(middayMs).toLocaleDateString('en-CA', { timeZone: tz })
            : moment(middayMs).format('YYYY-MM-DD');
    } catch (_) {
        dateStr = moment(middayMs).format('YYYY-MM-DD');
    }
    const ranges = allowlist[dateStr];
    const dateOpen = Array.isArray(ranges) ? ranges.length > 0 : !!ranges;

    if (selectedDeliveryMode === 'in_person') return dateOpen;
    if (selectedDeliveryMode === 'virtual' || selectedDeliveryMode === 'phone') return weeklyOpen;
    return weeklyOpen || dateOpen;
}

/** Offset (ms) between UTC and `tz` at the given instant. */
function tzOffsetMs(unixMs, tz) {
    const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = {};
    for (const part of dtf.formatToParts(new Date(unixMs))) p[part.type] = part.value;
    const hour = p.hour === '24' ? 0 : Number(p.hour);
    const asUTC = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second));
    return asUTC - unixMs;
}

/**
 * [startUnix, endUnix] for the CLINIC calendar day a carousel cell falls in.
 *
 * A cell's unixTime is the VIEWER's local midnight, so a viewer far from the
 * clinic would otherwise query the wrong clinic day (and could clip late slots).
 * Anchor at midday so the clinic date matches the cell's label, then return that
 * clinic day's UTC bounds. No-op for same-timezone viewers; falls back to the
 * viewer-local day if no clinic timezone is configured.
 */
function clinicDayBounds(unixTime) {
    const tz = clinicTimezone();
    const middayMs = (unixTime + 43200) * 1000;
    if (!tz) {
        return [moment(middayMs).startOf('day').unix(), moment(middayMs).endOf('day').unix()];
    }
    try {
        const dateStr = new Date(middayMs).toLocaleDateString('en-CA', { timeZone: tz });
        const naive = Date.parse(`${dateStr}T00:00:00Z`);
        let off = tzOffsetMs(naive, tz);
        let startMs = naive - off;
        off = tzOffsetMs(startMs, tz); // re-resolve at the corrected instant (DST safety)
        startMs = naive - off;
        const endMs = startMs + 24 * 3600 * 1000 - 1000;
        return [Math.floor(startMs / 1000), Math.floor(endMs / 1000)];
    } catch (_) {
        return [moment(middayMs).startOf('day').unix(), moment(middayMs).endOf('day').unix()];
    }
}

export async function loadTimeCarousel() {
    setAppointmentDateRange(getAppoitmentsDatetime());

    appointmentDateRange.dateArr.forEach((item) => {
        let temp = appointmentDateRange.monthArr.find((month) => {
            return month.month === item.month && month.year === item.year;
        });
        item.monthId = temp.id;
    });

    setLimitRightCarousel(Math.floor(appointmentDateRange.dateArr.length / 5));

    const carouselWrapper = document.querySelector(
        '.selectTime-carousel__list'
    );
    carouselWrapper.innerHTML = '';

    const locationAvails = form?.appointment_channel?.location_availabilities;
    const locationAvailability = locationAvails?.find(a => a.location_id === selectedLocation)?.availability ?? null;

    let i = 0;
    let len = appointmentDateRange.dateArr.length;
    let innerHTMLContentArr = [];
    while (i < len) {
        const disabledClass = dayCellIsAvailable(
            appointmentDateRange.dateArr[i].unixTime,
            locationAvailability,
            form?.appointment_channel?.availability_hours
        ) ? '' : 'disabled';

        innerHTMLContentArr.push(`
            <div class="selectTime-carousel__item ${disabledClass}">
                <button class="button--no-style" data-datetime-value="${appointmentDateRange.dateArr[i].unixTime}">
                    <div class="date" style="background-color: ${primaryColor}0f;"><span>${appointmentDateRange.dateArr[i].date}</span></div>
                    <div class="day"><span>${appointmentDateRange.dateArr[i].day}</span></div>
                </button>
            </div>
        `);
        i++;
    }
    carouselWrapper.insertAdjacentHTML(
        'beforeend',
        innerHTMLContentArr.join('')
    );

    await markDaysWithNoSlotsDisabled('.selectTime__container');
}

/**
 * Generation counter for the fully-booked sweep. Bumped on every render so a
 * background month chunk from a previous provider/service selection can't
 * disable days on the current carousel (`getFullyBookedDays` is the one slots
 * call with no axios cancel token).
 */
let fullyBookedRunId = 0;

/** Split the carousel's day cells into calendar-month chunks. */
function chunkDaysByMonth(days) {
    const chunks = [];
    let current = null;
    days.forEach((day) => {
        if (!current || current.month !== day.month || current.year !== day.year) {
            current = { month: day.month, year: day.year, days: [] };
            chunks.push(current);
        }
        current.days.push(day);
    });
    return chunks;
}

/**
 * Mark day circles as disabled when they have no slots (fully-booked-days API).
 *
 * The server computes this day-by-day with no cache, so asking for the whole
 * carousel at once cost ~0.09s/day (16s+ on a 150-day horizon) and blocked the
 * calendar render. Fetch one calendar month at a time instead: await only the
 * first month so the grid paints immediately, then fill in the later months in
 * the background as their responses land.
 * @param {string} containerSelector - e.g. '.selectTime__container' or '.selectOnlyDate__container'
 */
async function markDaysWithNoSlotsDisabled(containerSelector) {
    if (!form?.appointment_channel?.id) return;

    const daysToCheck = appointmentDateRange.dateArr;
    if (daysToCheck.length === 0) return;

    const runId = ++fullyBookedRunId;

    const listAddons = selectedAddons.map(addon => `procedure_addon_ids[]=${addon.id}`);
    const procedureIds = resolveProcedureIdsForNextSlot();
    const listProcedure = procedureIds.map((id) => `procedure_ids[]=${id}`);

    const baseParams = {
        procedure_ids: listProcedure?.join('&') || [],
        location_id: selectedLocation,
        ...(selectedAddons.length > 0 && { procedure_addon_ids: listAddons.join('&') }),
        staff_id: getStaffIdForHostType(),
    };

    const chunks = chunkDaysByMonth(daysToCheck);

    async function fetchAndMarkChunk(chunk) {
        // Clinic-day bounds so the range fully covers every clinic day in the
        // chunk, regardless of the viewer's timezone.
        const startTime = clinicDayBounds(chunk.days[0].unixTime)[0];
        const endTime = clinicDayBounds(chunk.days[chunk.days.length - 1].unixTime)[1];

        let data;
        try {
            data = await getFullyBookedDays(form.appointment_channel.id, {
                ...baseParams,
                start_time: startTime,
                end_time: endTime,
            });
        } catch (_) {
            return;
        }

        if (runId !== fullyBookedRunId) return; // superseded by a newer render
        applyFullyBookedMarks(containerSelector, data?.fully_booked_days || [], data?.timezone || null);
    }

    // First month blocks the render; the rest stream in without holding the UI.
    await fetchAndMarkChunk(chunks[0]);

    (async () => {
        for (let i = 1; i < chunks.length; i++) {
            if (runId !== fullyBookedRunId) return;
            await fetchAndMarkChunk(chunks[i]);
        }
    })();
}

/** Disable the day cells whose clinic-tz date is in `fullyBookedDates`. */
function applyFullyBookedMarks(containerSelector, fullyBookedDates, apiTimezone) {
    if (!fullyBookedDates.length) return;

    const root = document.querySelector(containerSelector);
    if (!root) return;

    // Use the same timezone the API used so our date strings match exactly
    const tzForMatch = apiTimezone
        || form?.appointment_channel?.location_availabilities?.find((a) => a.location_id === selectedLocation)?.availability?.timezone
        || form?.appointment_channel?.availability_timezone
        || null;

    function dateStrForUnix(unixTime) {
        // Carousel cells are built at the VIEWER's local midnight. Converting
        // midnight to the clinic timezone lands on the PREVIOUS clinic day for
        // viewers far east of the clinic (e.g. Europe), which shifted the
        // fully-booked-day marking by one day vs the cell's displayed date.
        // Anchor at midday (+12h) so the clinic-tz date always matches the
        // cell's label regardless of the viewer's timezone.
        const middayMs = (unixTime + 43200) * 1000;
        if (tzForMatch) {
            try {
                return new Date(middayMs).toLocaleDateString('en-CA', { timeZone: tzForMatch });
            } catch (_) {
                return moment(middayMs).format('YYYY-MM-DD');
            }
        }
        return moment(middayMs).format('YYYY-MM-DD');
    }

    const fullyBookedSet = new Set(fullyBookedDates);

    // Find every day button in the container and disable if its date (in API TZ) is fully booked
    root.querySelectorAll('.selectTime-carousel__item button[data-datetime-value]').forEach((btn) => {
        const raw = btn.getAttribute('data-datetime-value');
        const unixTime = raw === null || raw === '' ? null : parseInt(raw, 10);
        if (Number.isNaN(unixTime)) return;

        const dateStr = dateStrForUnix(unixTime);
        if (!fullyBookedSet.has(dateStr)) return;

        const item = btn.closest('.selectTime-carousel__item');
        if (item) item.classList.add('disabled');
    });
}

function clearOTPState() {
    clearOTPTimer();

    const otpInputs = document.querySelectorAll('.validateDigits__wrapper--input-field');
    otpInputs.forEach(input => {
        input.value = '';
    });
}

export async function handleEditPhone() {
    clearOTPState();

    const { is_phone_otp_enabled } = form || {}
    if (is_phone_otp_enabled && isBookAppointment) {
        navigate(SCREEN.REQUEST_CONSULT);
        return
    }

    newPhoneUI();
    await navigate(SCREEN.PHONE);

    phoneListeners();

    if (params('mobile')) {
        doFoCus(
            document.querySelector(
                '.phone__container .phone--input'
            )
        );
    } else {
        setTimeout(() => {
            document
                .querySelector('.phone__container .phone--input')
                .focus();
        }, 400);
    }
}

function getStaffIdForHostType() {
    const hostType = form?.appointment_channel?.host;

    if (hostType === 'round_robin' || hostType === 'collective') {
        return null;
    }

    // "Any Provider" mode: fetch merged slots across all providers
    if (providerId === -1) {
        return null;
    }

    const selectedProviderId = providerId || staffId;

    const staff = providers.find((provider) =>
        provider.id === selectedProviderId
    );

    return staff?.id;
}

/**
 * Resolve procedure IDs for the next-available-slot API when the channel has a single or preselected procedure.
 * API requires procedures; use selectedProcedures, then procedureIdSelected, then channel procedures.
 */
function resolveProcedureIdsForNextSlot() {
    if (selectedProcedures?.length > 0) {
        return selectedProcedures;
    }
    if (procedureIdSelected) {
        return [procedureIdSelected];
    }
    const channelProcedures = form?.appointment_channel?.procedures;
    if (channelProcedures?.length === 1) {
        return [channelProcedures[0].id];
    }
    if (proceduresOfAppointmentChannel?.length > 0) {
        return proceduresOfAppointmentChannel.map((p) => p.id);
    }
    if (channelProcedures?.length > 0) {
        return channelProcedures.map((p) => p.id);
    }
    return [];
}

export async function loadAppoimentsList(selectedUnixDatetime, isSpitPage = false) {
    document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''} .appointment-list-loading-screen`)?.classList.remove('hidden');
    let listAddons = selectedAddons.map(addon => {
        return `procedure_addon_ids[]=${addon.id}`;
    });


    const listProcedure = (selectedProcedures || [])?.map(procedure => `procedure_ids[]=${procedure}`);

    try {
        let availableAppointments = [];
        cancelRequest();
        const provider = providers.find((i) => i.id === providerId);
        // Query the CLINIC's calendar day for this cell, not the viewer's local
        // day — so far-away viewers fetch the correct day's full slot range.
        const [slotWindowStart, slotWindowEnd] = clinicDayBounds(selectedUnixDatetime);
        let res = await getTimeSlots({
            id: form.appointment_channel.id,
            start_time: slotWindowStart,
            end_time: slotWindowEnd,
            procedure_ids: listProcedure?.join('&') || [],
            location_id: selectedLocation,
            ...(selectedAddons.length > 0 && {
                procedure_addon_ids: listAddons.join('&')
            }),
            staff_id: getStaffIdForHostType()
        });
        setDataSlot(res);
        availableAppointments = res.data.slots;
        let innerHTMLContentArr = [];
        let innerHTMLContentStr = '';
        let selectTimeContainer = document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : '.selectTime__container'} .selectTime__list`);
        selectTimeContainer.innerHTML = '';

        if (!availableAppointments || availableAppointments.length === 0) {
            let nextAvailableAppointmentRes = [];
            try {
                const procedureIdsForNext = resolveProcedureIdsForNextSlot();
                const proceduresParam = procedureIdsForNext.length
                    ? procedureIdsForNext.map((id) => `procedures[]=${id}`).join('&')
                    : '';
                nextAvailableAppointmentRes = await getNextAvailableSlot(form.appointment_channel.id, {
                    procedures: proceduresParam,
                    ...(selectedAddons.length > 0 && {
                        procedure_addon_ids: listAddons.join('&')
                    }),
                    location_id: selectedLocation,
                    staff_id: getStaffIdForHostType()
                });
            } catch (error) {
                //
            }

            let nextAvailableAppointmentData = nextAvailableAppointmentRes?.data;

            if (!nextAvailableAppointmentData) {
                innerHTMLContentStr = `
                <div class="selectTime__item--empty">
                    <div class="selectTime__calender-icon" style="background-color: ${primaryColor}0f;">
                        <i data-feather="calendar"></i>
                    </div>
                    <div class="selectTime__content--empty">
                        <div class="selectTime__title--empty">
                            <span>${getProviderFullName()}</span> ${t('all_booked').replace('{provider}', '')}
                        </div>
                    </div>
                    </div>
                `;
                if (selectTimeContainer) {
                    selectTimeContainer.style.background = "#fff";
                }

                selectTimeContainer.insertAdjacentHTML('beforeend', innerHTMLContentStr);
                feather.replace();
                document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .appointment-list-loading-screen')?.classList.add('hidden');
                return;
            }

            innerHTMLContentStr = `
                <div class="selectTime__item--empty">
                    <div class="selectTime__calender-icon" style="background-color: ${primaryColor}0f;">
                        <i data-feather="calendar"></i>
                    </div>
                    <div class="selectTime__content--empty">
                        <div class="selectTime__title--empty">
                            <span>${getProviderFullName()}</span> ${t('all_booked').replace('{provider}', '')}
                        </div>
                        <div class="selectTime__subtitle--empty">
                            ${t('choose_another_day')}
                        </div>
                    </div>
                    <button class="selectTime__button-next-appointment button--no-style">
                        ${t('next_available').replace('{date}', moment(nextAvailableAppointmentData.start * 1000).format('MMMM Do'))}
                    </button>
                </div>
            `;

            if (selectTimeContainer) {
                selectTimeContainer.style.background = "#fff";
            }

            selectTimeContainer.insertAdjacentHTML('beforeend', innerHTMLContentStr);
            document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .selectTime__button-next-appointment').addEventListener('click', () => {
                if (appointmentChannelId) {
                    clickOnlyDateCarouselItem(nextAvailableAppointmentData.start);
                } else {
                    clickDateCarouselItem(nextAvailableAppointmentData.start);
                }
            }, { once: true });
            document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .appointment-list-loading-screen')?.classList.add('hidden');
        } else {
            let i = 0,
                len = availableAppointments.length;
            while (i < len) {
                innerHTMLContentArr.push(`
                    <div class="selectTime__item w-100 d-flex" data-time-value="${availableAppointments[i].start}/${availableAppointments[i].end}" data-staff-id="${availableAppointments[i]?.assigned_staff_id}">
                        <div class="selectTime__icon-time-wrapper d-flex">
                            <div class="selectTime__clock-icon" style="background-color:${primaryColor}0f;">
                                <i data-feather="clock"></i>
                            </div>
                            <div class="selectTime__time">
                            ${(() => {
                        try {
                            return moment(availableAppointments[i].start * 1000).format('hh:mm A');
                        } catch (error) {
                            return t('time_unavailable');
                        }
                    })()}
                            </div>
                        </div>
                        <div class="selectTime__right-arrow" data-selected-time="${availableAppointments[i].start
                    }">
                            <i data-feather="arrow-right"></i>
                        </div>
                        <div class="selectTime__spinner hidden">
                            <svg width="24" height="24" stroke="${primaryColor}" data-loading="lazy" data-src="assets/icons/loading-spinner.svg"></svg>
                        </div>
                    </div>
                `);
                i++;
            }
            innerHTMLContentStr = innerHTMLContentArr.join('');
            selectTimeContainer.insertAdjacentHTML('beforeend', innerHTMLContentStr);

            if (selectTimeContainer) {
                // The split times screen must stay opaque: screens are stacked, never
                // hidden, so a transparent list here lets the calendar screen's date
                // grid show through the gaps between slot rows if z-order ever slips.
                // #f4f4f4 matches the wrappers' background, so it looks identical.
                selectTimeContainer.style.background = isSpitPage ? "#f4f4f4" : "unset";
            }

            // Bind click event to items
            document.querySelectorAll(`${isSpitPage ? ".selectOnlyTime__container" : '.selectTime__list'} .selectTime__item`).forEach((element) => {
                element.addEventListener('click', async () => {
                    if (visitorId) {
                        updateVisitorAction(visitorId, 'selected-timeslot', {
                            timeslot: element.dataset.timeValue,
                            ...(locationId && { location_id: locationId }),
                        }).catch(error => {
                            console.error('Failed to track timeslot selection:', error);
                        });
                    }

                    if (isBookAppointment) {
                        setSelectedAppointmentUnixTime(element.dataset.timeValue);
                        setAssignedStaffId(element?.dataset?.staffId || null);
                        setLocationId(locationId);

                        const collect_patient_information_first_before_scheduling = form?.appointment_channel?.collect_patient_information_first_before_scheduling;
                        const shouldCollectPatientInfoFirstForWidget = leadInfo && leadInfo.phone && collect_patient_information_first_before_scheduling && appointmentChannelId;
                        if (shouldCollectPatientInfoFirstForWidget) {
                            const { is_phone_otp_enabled } = form || {};

                            if (!is_phone_otp_enabled && form?.appointment_channel?.id) {
                                loadSpinnerIcon();
                                try {
                                    const validPhone = parsePhoneNumberFromString(
                                        leadInfo.phone,
                                        leadInfo?.country || 'US'
                                    );
                                    const bookingTokenResponse = await createBookingToken(
                                        form.appointment_channel.id,
                                        {
                                            phone: validPhone.number,
                                            ...(visitorId && { visitor_id: visitorId }),
                                        }
                                    );
                                    if (bookingTokenResponse?.data?.access_token) {
                                        setAccessToken(bookingTokenResponse.data.access_token);
                                    }
                                    const lead = bookingTokenResponse?.data?.lead;
                                    if (lead) {
                                        setLeadInfo(lead);
                                        if (lead?.text_id) {
                                            setLeadTextId(lead.text_id);
                                        }
                                    }
                                } catch (error) {
                                    console.error('Failed to create booking token:', error);
                                } finally {
                                    loadSpinnerIcon(false);
                                }
                                await appointmentItemsClickEventHandler(element.dataset.timeValue, null);
                                return;
                            }

                            if (otpAccessToken) {
                                setAccessToken(otpAccessToken);
                                await appointmentItemsClickEventHandler(element.dataset.timeValue, null);
                                return;
                            }

                            setPhone(leadInfo.phone);
                            setSelectedCountry(leadInfo?.country || 'US');

                            loadSpinnerIcon();
                            const validPhone = parsePhoneNumberFromString(
                                leadInfo.phone,
                                leadInfo?.country || 'US'
                            );

                            if (visitorId) {
                                updateVisitorAction(visitorId, '2fa-number-input', {
                                    phone: validPhone.number,
                                }).catch(error => {
                                    console.error('Failed to track phone input:', error);
                                });
                            }

                            if (isOTPTimeOut) {
                                loadSpinnerIcon();
                                await sendOTPCodeWithTimer();
                            }
                            document.getElementById('validateDigits0').focus();
                            navigate(SCREEN.VALIDATE_DIGITS);
                            const phoneNumObj = parsePhoneNumberFromString(
                                leadInfo.phone,
                                'US'
                            );
                            loadSpinnerIcon(false);
                            if (
                                document.querySelector(
                                    '.validateDigits--sub-title strong'
                                )
                            ) {
                                document.querySelector(
                                    '.validateDigits--sub-title strong'
                                ).innerHTML = phoneNumObj.number;
                            }

                            if (isFlowForCalendar) {
                                const editPhoneBtn = document.querySelector('.edit-phone-btn');
                                if (editPhoneBtn) {
                                    editPhoneBtn.style.display = 'inline';
                                    editPhoneBtn.addEventListener('click', handleEditPhone);
                                }
                            }
                            loadSpinnerIcon(false);
                            return;
                        }
                        newPhoneUI();
                        await navigate(SCREEN.PHONE);

                        if (params('mobile')) {
                            doFoCus(
                                document.querySelector(
                                    '.phone__container .phone--input'
                                )
                            );
                        } else {
                            setTimeout(() => {
                                document
                                    .querySelector('.phone__container .phone--input')
                                    .focus();
                            }, 400);
                        }
                    } else {
                        await appointmentItemsClickEventHandler(element.dataset.timeValue, null);
                    }

                }
                );
            });
            document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .appointment-list-loading-screen')?.classList.add('hidden');
        }

        // eslint-disable-next-line no-undef
        feather.replace();

    } catch (error) {
        const isMissingProcedureIds = error?.response?.data?.message?.includes('procedure ids');
        const isLoading = document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .appointment-list-loading-screen')?.classList.contains('hidden');
        if (!isLoading && !isMissingProcedureIds) return;

        let innerHTMLContentStr = `
                <div class="selectTime__item--empty">
                    <div class="selectTime__calender-icon" style="background-color: ${primaryColor}0f;">
                        <i data-feather="calendar"></i>
                    </div>
                    <div class="selectTime__content--empty">
                        <div class="selectTime__title--empty">
                            <span>${getProviderFullName()}</span> ${t('all_booked').replace('{provider}', '')}
                        </div>
                        <div class="selectTime__subtitle--empty">
                            ${t('choose_another_day')}
                        </div>
                    </div>
                </div>
            `;

        let selectTimeContainer = document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : '.selectTime__container'} .selectTime__list`);
        selectTimeContainer.innerHTML = '';
        if (selectTimeContainer) {
            selectTimeContainer.style.background = "#fff";
        }
        selectTimeContainer.insertAdjacentHTML('beforeend', innerHTMLContentStr);
        feather.replace();
        document.querySelector(`${isSpitPage ? ".selectOnlyTime__container" : ''}` + ' .appointment-list-loading-screen')?.classList.add('hidden');
    }
}

export async function loadAvailableMonths() {
    let availableMonthItemsDom = document.querySelector('.selectTime__info');
    availableMonthItemsDom.innerHTML = '';
    let i = 0;
    let len = appointmentDateRange.monthArr.length;
    let htmlContentArr = [];
    let selectTimeCarouselItemWidth = document
        .querySelector('.selectTime-carousel__item')
        .getBoundingClientRect().width;

    // Check which carousel items are disabled so we can hide month labels
    // for months where every day is disabled
    const allCarouselItems = document.querySelectorAll(
        '.selectTime__container .selectTime-carousel__list .selectTime-carousel__item'
    );
    let dateIndex = 0;

    while (i < len) {
        const monthDateCount = appointmentDateRange.monthArr[i].dateCount;

        // Check if all days in this month are disabled
        let allDisabled = true;
        for (let j = dateIndex; j < dateIndex + monthDateCount && j < allCarouselItems.length; j++) {
            if (!allCarouselItems[j].classList.contains('disabled')) {
                allDisabled = false;
                break;
            }
        }

        const monthLabel = allDisabled
            ? ''
            : convertMonthFormat(appointmentDateRange.monthArr[i].month, 'long');

        htmlContentArr.push(
            `<div class="selectTime__info--available-month" style="flex-basis: ${selectTimeCarouselItemWidth *
            monthDateCount
            }px">
                ${monthLabel}
            </div>`
        );
        dateIndex += monthDateCount;
        i++;
    }
    availableMonthItemsDom.insertAdjacentHTML(
        'beforeend',
        htmlContentArr.join('')
    );
}

export async function renderPageSelectTime(selectedUnixDatetime) {
    loadAppoimentsList(selectedUnixDatetime);
    loadTimeCarousel();
}

function cancelRequest() {
    try {
        if (requestsGetSlotsTime?.length > 0) {
            requestsGetSlotsTime.forEach(item => {
                item.cancel && item.cancel();
            });
            setRequestsGetSlotsTime([]);
        }
        if (nextRequestsGetSlotsTime?.length > 0) {
            nextRequestsGetSlotsTime.forEach(item => {
                item.cancel && item.cancel();
            });
            setNextRequestsGetSlotsTime([]);
        }
    } catch (error) {
        console.error('Error cancelling requests:', error);
    }
}

const renderTitleDayOfWeek = () => {
    const arrDayOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    const innerHTMLContentArr = arrDayOfWeek.map(week => {
        return `
        <div class="selectTime-carousel__item">
           <span>${week}</span>
        </div>
        `;
    });

    return innerHTMLContentArr.join('');
}

export async function loadOnlyTimeCarousel() {
    setAppointmentDateRange(getAppoitmentsDatetime());

    appointmentDateRange.dateArr.forEach((item) => {
        let temp = appointmentDateRange.monthArr.find((month) => {
            return month.month === item.month && month.year === item.year;
        });
        item.monthId = temp.id;
    });

    setLimitRightCarousel(Math.floor(appointmentDateRange.dateArr.length / 7));

    const carouselWrapper = document.querySelector(
        '.selectOnlyDate__container  .selectOnlyTime-carousel__list'
    );
    carouselWrapper.innerHTML = '';

    const weekTitle = document.querySelector(
        '.selectOnlyDate__container  .selectTime__list-title'
    );

    let indexMonth = 0;
    let lenMonth = appointmentDateRange.monthArr.length;
    let innerHTMLContentArrMonth = [];
    if (weekTitle) {
        weekTitle.innerHTML = "";
    }
    weekTitle.insertAdjacentHTML(
        'beforeend',
        `
        <div class="selectTime-carousel__item-week">
            <div class="list-title-header">
            ${renderTitleDayOfWeek()}
            </div>
        </div>
        `
    );

    while (indexMonth < lenMonth) {
        innerHTMLContentArrMonth.push(`
            <div class="selectTime-carousel__item-Month" data-month=${appointmentDateRange.monthArr[indexMonth].month}>
                <div class="selectTime__info--available-month">
                ${renderDayOfMonth(appointmentDateRange.monthArr[indexMonth])}
                </div>
            </div>
        `);
        indexMonth++;
    }

    carouselWrapper.insertAdjacentHTML(
        'beforeend',
        innerHTMLContentArrMonth.join('')
    );

    await markDaysWithNoSlotsDisabled('.selectOnlyDate__container');
}

const renderDayOfMonth = (monthCurrent) => {
    // Filter appointment dates for the current month and year
    const filteredDates = appointmentDateRange.dateArr.filter(item => item.month === monthCurrent.month && item.year === monthCurrent.year);

    // Split filtered dates into weeks
    const weeks = [];
    let currentWeek = [];
    filteredDates.forEach(date => {
        if (date.day === "Mon" && currentWeek.length > 0) {
            weeks.push(currentWeek);
            currentWeek = [];
        }
        currentWeek.push(date);
    });
    if (currentWeek.length > 0) {
        weeks.push(currentWeek);
    }
    const currentUnixTime = moment().startOf('day').unix();

    const location_availabilities = form?.appointment_channel?.location_availabilities;
    const location_availability = location_availabilities?.find(a => a.location_id === selectedLocation)?.availability ?? null;

    // Generate HTML content for each week
    const innerHTMLContentArr = weeks.map(week => {
        const temp = [];
        week.forEach(day => {
            const isLessThanCurrent = day.unixTime < currentUnixTime;

            const availabilityClass = dayCellIsAvailable(
                day.unixTime,
                location_availability,
                form?.appointment_channel?.availability_hours
            ) ? '' : 'disabled';

            if (week[0].day !== "Mon" && week.length !== 7 && week[week.length - 1].day === "Sun" && temp.length === 0) {
                temp.push(...(Array(7 - week.length).fill(`
                    <div class="selectTime-carousel__item" style="visibility: hidden;">
                        <button class="button--no-style">
                            <div class="date" ><span></span></div>
                            <div class="day"><span></span></div>
                        </button>
                    </div>
                    `)));
            }

            temp.push(`
                <div class="selectTime-carousel__item ${isLessThanCurrent ? "disabled" : ""} ${availabilityClass}">
                    <button class="button--no-style" data-datetime-value="${day.unixTime}">
                        <div class="date" style="background-color: ${primaryColor}0f;"><span>${day.date}</span></div>
                    </button>
                </div>
            `);

        });
        if (temp?.length !== 7) {
            temp.push(...(Array(7 - week.length).fill(`
                    <div class="selectTime-carousel__item" style="visibility: hidden;">
                        <button class="button--no-style">
                            <div class="date" ><span></span></div>
                            <div class="day"><span></span></div>
                        </button>
                    </div>
                    `)));
        }
        return `
            <div class="selectTime-carousel__item-week ${temp?.length !== 7 ? "notFullWeek" : ""}">
                ${temp.join('')}
            </div>
        `;
    });

    return innerHTMLContentArr.join('');
};

function formatUnixTime(unixTime, timezone) {
    const date = new Date(unixTime * 1000);

    const options = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
    };

    try {
        return new Intl.DateTimeFormat('en-US', options).format(date);
    } catch (error) {
        return new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'UTC' }).format(date) + ' UTC';
    }
}
